/**
 * PortalVerticalPerformance — Vertical Performance dashboard
 *
 * Shows how each vertical is performing based on real session_events data.
 * Includes summary stats, sortable performance matrix, and auto-tune suggestions.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { subDays } from 'date-fns';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet, supabasePatch } from '@/utils/supabaseRest';
import { Button } from '@/components/ui/button';
import {
  BarChart2, TrendingUp, TrendingDown, ArrowUpDown,
  Loader2, CheckCircle, AlertTriangle, Lightbulb,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ───────────────────────────────────────────

type TimeRange = '7' | '30' | '90';
type SortDir = 'asc' | 'desc';

interface SessionEvent {
  event_type: 'presented' | 'rejected' | 'converted' | 'session_end';
  vertical_id: string;
  score: number | null;
  revenue: number | null;
  created_at: string;
}

interface Vertical {
  id: string;
  name: string;
  cluster: string | null;
  payout_score: number | null;
  conversion_score: number | null;
  is_active: boolean;
}

interface VerticalStats {
  verticalId: string;
  name: string;
  cluster: string | null;
  presented: number;
  converted: number;
  rejected: number;
  convRate: number;
  avgScore: number;
  totalRevenue: number;
  payoutScore: number;
  conversionScore: number;
  status: 'above' | 'average' | 'below';
}

interface Suggestion {
  verticalId: string;
  name: string;
  actualCvr: number;
  expectedCvr: number;
  currentPayout: number;
  suggestedPayout: number;
  direction: 'increase' | 'decrease';
}

// ─── Cluster colors ──────────────────────────────────

const CLUSTER_COLORS: Record<string, string> = {
  insurance: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  finance: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  legal: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  health: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  home: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  education: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  default: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

function clusterStyle(cluster: string | null): string {
  if (!cluster) return CLUSTER_COLORS.default;
  return CLUSTER_COLORS[cluster.toLowerCase()] || CLUSTER_COLORS.default;
}

// ─── Component ───────────────────────────────────────

export default function PortalVerticalPerformance() {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortCol, setSortCol] = useState<keyof VerticalStats>('convRate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());

  // ── Data load ──
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const days = parseInt(timeRange);
        const since = subDays(new Date(), days).toISOString();

        const [eventsData, verticalsData] = await Promise.all([
          supabaseGet<SessionEvent[]>(
            `session_events?select=event_type,vertical_id,score,revenue,created_at&created_at=gte.${encodeURIComponent(since)}`
          ),
          supabaseGet<Vertical[]>(
            'verticals?select=id,name,cluster,payout_score,conversion_score,is_active&is_active=eq.true'
          ),
        ]);

        if (cancelled) return;
        setEvents(eventsData || []);
        setVerticals(verticalsData || []);
      } catch (err) {
        console.warn('VerticalPerformance: load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [timeRange]);

  // ── Compute stats per vertical ──
  const verticalStats = useMemo<VerticalStats[]>(() => {
    if (!verticals.length) return [];

    const statsMap = new Map<string, {
      presented: number; converted: number; rejected: number;
      totalScore: number; totalRevenue: number;
    }>();

    // Initialize all verticals
    for (const v of verticals) {
      statsMap.set(v.id, { presented: 0, converted: 0, rejected: 0, totalScore: 0, totalRevenue: 0 });
    }

    // Aggregate events
    for (const evt of events) {
      const s = statsMap.get(evt.vertical_id);
      if (!s) continue;

      if (evt.event_type === 'presented') {
        s.presented++;
        if (evt.score != null) s.totalScore += evt.score;
      } else if (evt.event_type === 'converted') {
        s.converted++;
        if (evt.revenue != null) s.totalRevenue += evt.revenue;
      } else if (evt.event_type === 'rejected') {
        s.rejected++;
      }
    }

    // Build stats array
    const result: VerticalStats[] = [];
    for (const v of verticals) {
      const s = statsMap.get(v.id)!;
      const convRate = s.presented > 0 ? (s.converted / s.presented) * 100 : 0;
      result.push({
        verticalId: v.id,
        name: v.name,
        cluster: v.cluster,
        presented: s.presented,
        converted: s.converted,
        rejected: s.rejected,
        convRate,
        avgScore: s.presented > 0 ? s.totalScore / s.presented : 0,
        totalRevenue: s.totalRevenue,
        payoutScore: v.payout_score ?? 0,
        conversionScore: v.conversion_score ?? 0,
        status: 'average', // placeholder, computed below
      });
    }

    // Compute average conv rate (only verticals with >= 1 presentation)
    const withData = result.filter(r => r.presented > 0);
    const avgConvRate = withData.length > 0
      ? withData.reduce((sum, r) => sum + r.convRate, 0) / withData.length
      : 0;

    for (const r of result) {
      if (r.presented === 0) {
        r.status = 'average';
      } else if (r.convRate > avgConvRate * 1.2) {
        r.status = 'above';
      } else if (r.convRate < avgConvRate * 0.8) {
        r.status = 'below';
      } else {
        r.status = 'average';
      }
    }

    return result;
  }, [events, verticals]);

  // ── Sorted stats ──
  const sortedStats = useMemo(() => {
    const arr = [...verticalStats];
    arr.sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [verticalStats, sortCol, sortDir]);

  // ── Summary metrics ──
  const summary = useMemo(() => {
    const active = verticalStats.length;
    const totalPresented = verticalStats.reduce((s, v) => s + v.presented, 0);
    const totalConverted = verticalStats.reduce((s, v) => s + v.converted, 0);
    const overallCvr = totalPresented > 0 ? (totalConverted / totalPresented) * 100 : 0;

    const withMinPresentations = verticalStats.filter(v => v.presented >= 5);
    const highest = withMinPresentations.length > 0
      ? withMinPresentations.reduce((best, v) => v.convRate > best.convRate ? v : best)
      : null;
    const lowest = withMinPresentations.length > 0
      ? withMinPresentations.reduce((worst, v) => v.convRate < worst.convRate ? v : worst)
      : null;

    return { active, totalPresented, overallCvr, highest, lowest };
  }, [verticalStats]);

  // ── Auto-tune suggestions ──
  const suggestions = useMemo<Suggestion[]>(() => {
    const results: Suggestion[] = [];
    for (const v of verticalStats) {
      if (v.presented < 5 || v.conversionScore === 0) continue;

      const expectedCvr = v.conversionScore; // conversion_score is the expected % rate
      const actualCvr = v.convRate;
      const ratio = actualCvr / expectedCvr;

      // Significant deviation: > 50% above or below expected
      if (ratio > 1.5) {
        const bump = Math.round(v.payoutScore * Math.min(ratio, 2));
        results.push({
          verticalId: v.verticalId,
          name: v.name,
          actualCvr,
          expectedCvr,
          currentPayout: v.payoutScore,
          suggestedPayout: Math.max(bump, v.payoutScore + 1),
          direction: 'increase',
        });
      } else if (ratio < 0.5) {
        const drop = Math.round(v.payoutScore * Math.max(ratio, 0.5));
        results.push({
          verticalId: v.verticalId,
          name: v.name,
          actualCvr,
          expectedCvr,
          currentPayout: v.payoutScore,
          suggestedPayout: Math.max(drop, 1),
          direction: 'decrease',
        });
      }
    }
    return results.sort((a, b) => {
      const aGap = Math.abs(a.actualCvr - a.expectedCvr);
      const bGap = Math.abs(b.actualCvr - b.expectedCvr);
      return bGap - aGap;
    });
  }, [verticalStats]);

  // ── Apply suggestion ──
  const applySuggestion = useCallback(async (s: Suggestion) => {
    setApplyingId(s.verticalId);
    try {
      await supabasePatch(
        `verticals?id=eq.${s.verticalId}`,
        { payout_score: s.suggestedPayout }
      );
      setAppliedIds(prev => new Set(prev).add(s.verticalId));
      // Update local state
      setVerticals(prev => prev.map(v =>
        v.id === s.verticalId ? { ...v, payout_score: s.suggestedPayout } : v
      ));
    } catch (err) {
      console.error('Failed to apply suggestion:', err);
    } finally {
      setApplyingId(null);
    }
  }, []);

  // ── Sort handler ──
  const handleSort = (col: keyof VerticalStats) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'above': return <span title="Above average" className="text-emerald-400">&#x1F7E2;</span>;
      case 'below': return <span title="Below average" className="text-red-400">&#x1F534;</span>;
      default: return <span title="Average" className="text-yellow-400">&#x1F7E1;</span>;
    }
  };

  const SortHeader = ({ col, label, className }: { col: keyof VerticalStats; label: string; className?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-emerald-400 transition-colors select-none ${className || ''}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortCol === col && (
          <ArrowUpDown className="h-3 w-3 text-emerald-400" />
        )}
      </span>
    </th>
  );

  return (
    <PortalLayout>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart2 className="h-6 w-6 text-emerald-400" />
              Vertical Performance
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Conversion rates, revenue, and auto-tune suggestions for each vertical
            </p>
          </div>

          {/* Time range picker */}
          <div className="flex items-center gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
            {(['7', '30', '90'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  timeRange === r ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <>
            {/* ─── Summary Stats ─── */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard
                label="Active Verticals"
                value={summary.active.toString()}
                sub="currently enabled"
              />
              <StatCard
                label="Total Presentations"
                value={summary.totalPresented.toLocaleString()}
                sub={`last ${timeRange} days`}
              />
              <StatCard
                label="Overall Conv. Rate"
                value={`${summary.overallCvr.toFixed(1)}%`}
                sub="across all verticals"
              />
              <StatCard
                label="Highest Performing"
                value={summary.highest?.name || '—'}
                sub={summary.highest ? `${summary.highest.convRate.toFixed(1)}% CVR` : 'min 5 presentations'}
                accent="emerald"
              />
              <StatCard
                label="Lowest Performing"
                value={summary.lowest?.name || '—'}
                sub={summary.lowest ? `${summary.lowest.convRate.toFixed(1)}% CVR` : 'min 5 presentations'}
                accent="red"
              />
            </div>

            {/* ─── Performance Matrix ─── */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800">
                <h2 className="text-lg font-semibold text-white">Vertical Performance Matrix</h2>
                <p className="text-xs text-slate-500 mt-1">Click any column header to sort</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-950/50">
                    <tr>
                      <SortHeader col="name" label="Vertical" />
                      <SortHeader col="cluster" label="Cluster" />
                      <SortHeader col="presented" label="Presented" />
                      <SortHeader col="converted" label="Converted" />
                      <SortHeader col="rejected" label="Rejected" />
                      <SortHeader col="convRate" label="Conv. Rate" />
                      <SortHeader col="avgScore" label="Avg Score" />
                      <SortHeader col="totalRevenue" label="Revenue" />
                      <SortHeader col="payoutScore" label="Payout Score" />
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {sortedStats.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-12 text-center text-slate-500">
                          No vertical data found for this period
                        </td>
                      </tr>
                    ) : (
                      sortedStats.map((v) => (
                        <tr key={v.verticalId} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-white">{v.name}</td>
                          <td className="px-4 py-3">
                            {v.cluster ? (
                              <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${clusterStyle(v.cluster)}`}>
                                {v.cluster}
                              </span>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-300 tabular-nums">{v.presented.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-emerald-400 tabular-nums">{v.converted.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-red-400 tabular-nums">{v.rejected.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm font-semibold tabular-nums">
                            <span className={v.convRate > 15 ? 'text-emerald-400' : v.convRate > 5 ? 'text-yellow-400' : 'text-red-400'}>
                              {v.convRate.toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-300 tabular-nums">{v.avgScore.toFixed(1)}</td>
                          <td className="px-4 py-3 text-sm text-emerald-400 tabular-nums">${v.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-sm text-slate-300 tabular-nums">{v.payoutScore}</td>
                          <td className="px-4 py-3 text-center">{statusIcon(v.status)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── Auto-Tune Suggestions ─── */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl">
              <div className="px-6 py-4 border-b border-slate-800 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-400" />
                <h2 className="text-lg font-semibold text-white">Auto-Tune Suggestions</h2>
                <span className="ml-2 text-xs text-slate-500">
                  Based on actual vs expected conversion rates
                </span>
              </div>
              <div className="p-4 space-y-3">
                {suggestions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500/50" />
                    <p>All verticals are performing within expected ranges</p>
                    <p className="text-xs mt-1">Suggestions appear when actual CVR deviates 50%+ from expected</p>
                  </div>
                ) : (
                  suggestions.map((s) => (
                    <div
                      key={s.verticalId}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        s.direction === 'increase'
                          ? 'bg-emerald-500/5 border-emerald-500/20'
                          : 'bg-red-500/5 border-red-500/20'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {s.direction === 'increase' ? (
                          <TrendingUp className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-white">
                            {s.name}:{' '}
                            <span className="text-slate-400">actual CVR</span>{' '}
                            <span className={s.direction === 'increase' ? 'text-emerald-400' : 'text-red-400'}>
                              {s.actualCvr.toFixed(1)}%
                            </span>{' '}
                            <span className="text-slate-400">vs expected</span>{' '}
                            <span className="text-slate-300">{s.expectedCvr.toFixed(1)}%</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Suggest {s.direction === 'increase' ? 'increasing' : 'decreasing'} payout_score from{' '}
                            <span className="text-slate-300">{s.currentPayout}</span> to{' '}
                            <span className={s.direction === 'increase' ? 'text-emerald-400' : 'text-red-400'}>
                              {s.suggestedPayout}
                            </span>
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={applyingId === s.verticalId || appliedIds.has(s.verticalId)}
                        onClick={() => applySuggestion(s)}
                        className={`ml-4 flex-shrink-0 ${
                          appliedIds.has(s.verticalId)
                            ? 'border-emerald-500/30 text-emerald-400'
                            : 'border-slate-700 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-400'
                        }`}
                      >
                        {applyingId === s.verticalId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : appliedIds.has(s.verticalId) ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Applied
                          </>
                        ) : (
                          'Apply Suggestion'
                        )}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}

// ─── Sub-components ──────────────────────────────────

function StatCard({ label, value, sub, accent }: {
  label: string;
  value: string;
  sub: string;
  accent?: 'emerald' | 'red';
}) {
  const valueColor = accent === 'emerald'
    ? 'text-emerald-400'
    : accent === 'red'
    ? 'text-red-400'
    : 'text-white';

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold mt-1 truncate ${valueColor}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
  );
}
